import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "locales";
import { ShareReceivedPayload } from "hooks/useShareTarget";
import useThreadPicker, { SAVED_MESSAGES_ID, ThreadItem } from "hooks/useThreadPicker";
import emitter, { EVENTS } from "lib/emitter";
import { setPendingShared } from "lib/pendingSharedFiles";
import providers from "lib/providers";
import "./style.scss";

interface PickerProps {
  files: File[];
  text?: string;
  onClose: () => void;
}

function formatSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The chat picker shown once a share arrives. Picking a chat stashes the files
 *  for that chat's composer and navigates there (open-and-prefill flow). */
function ShareTargetPicker({ files, text, onClose }: PickerProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { threads, loading } = useThreadPicker();
  const [query, setQuery] = useState("");

  // Object URLs for image previews — revoked on unmount.
  const previews = useMemo(
    () =>
      files.map((f) => ({
        file: f,
        isImage: f.type.startsWith("image/"),
        url: f.type.startsWith("image/") ? URL.createObjectURL(f) : "",
      })),
    [files]
  );
  useEffect(() => () => previews.forEach((p) => p.url && URL.revokeObjectURL(p.url)), [previews]);

  const filtered = query ? threads.filter((th) => th.name.toLowerCase().includes(query.toLowerCase())) : threads;

  // Single-flight: the Saved Messages path awaits a network resolve, during
  // which a double-click would run the whole tail twice (closing the picker
  // twice pops a second queued OS-share unseen).
  const pickingRef = useRef(false);
  const handlePick = async (thread: ThreadItem) => {
    if (pickingRef.current) return;
    pickingRef.current = true;
    // The synthetic Saved Messages entry has no thread yet. Try to resolve the
    // real self-thread id; if it doesn't exist yet (findPersonal is find-only
    // and 404s — the lazy thread is created on first message), fall back to a
    // `user:<id>` key. MessageInput consumes BOTH its currentThreadId and
    // `user:<userIds[0]>`, so the share lands even before the thread exists.
    let stashId = thread.id;
    if (thread.id === SAVED_MESSAGES_ID && thread.userId) {
      stashId = `user:${thread.userId}`;
      try {
        const selfThread = await providers.MessageThreadsProvider.findPersonal({ opponentId: thread.userId });
        if (selfThread?.id) stashId = String(selfThread.id);
      } catch {
        /* no self-thread yet — keep the user:<id> key */
      }
    }
    // Hand files + text to the target chat's composer, then open that chat.
    setPendingShared(stashId, { files, text });
    const path =
      thread.subjectType === "group" && thread.groupId
        ? // Always route a group via its General topic: a topics-enabled group's
          // bare /messages/group/:id renders the Topics overview (no composer),
          // which would strand the share. "general" resolves the same
          // topicId-IS-NULL thread (= thread.id) for both topics-on and -off groups.
          `/messages/group/${thread.groupId}/topic/general`
        : thread.userId
        ? `/messages/${thread.userId}`
        : `/messages/${thread.id}`;
    onClose();
    history.push(path);
    // If the user picked the chat already on screen, the push is a no-op and
    // MessageInput's currentThreadId doesn't change — nudge it to consume now.
    emitter.emit(EVENTS.SHARE_HANDOFF, { threadId: stashId });
    pickingRef.current = false;
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal__header">
          <h3>{t("Share to chat")}</h3>
          <button className="share-modal__close" onClick={onClose} type="button">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>

        {(previews.length > 0 || text) && (
          <div className="share-modal__preview">
            {previews.map((p, i) =>
              p.isImage ? (
                <img key={i} className="share-modal__thumb" src={p.url} alt={p.file.name} />
              ) : (
                <div key={i} className="share-modal__thumb share-modal__thumb--file">
                  <span className="share-modal__thumb-name">{p.file.name}</span>
                  <span className="share-modal__thumb-size">{formatSize(p.file.size)}</span>
                </div>
              )
            )}
            {text && <div className="share-modal__text">{text}</div>}
          </div>
        )}

        <div className="share-modal__search">
          <input
            type="text"
            placeholder={t("Search chats...")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="share-modal__list">
          {loading && <div className="share-modal__empty">{t("Loading...")}</div>}
          {!loading &&
            filtered.map((thread) => (
              <div key={thread.id} className="share-modal__item" onClick={() => handlePick(thread)}>
                <div className="share-modal__item-avatar">{thread.name.charAt(0).toUpperCase()}</div>
                <div className="share-modal__item-name">{thread.name}</div>
                <span className="share-modal__item-type">
                  {thread.subjectType === "group" ? t("Group") : t("Chat")}
                </span>
              </div>
            ))}
          {!loading && filtered.length === 0 && <div className="share-modal__empty">{t("No chats found")}</div>}
        </div>
      </div>
    </div>
  );
}

/** Globally mounted (like IncomingCallModal). Opens the picker when
 *  useShareTarget emits EVENTS.SHARE_RECEIVED. Renders nothing otherwise.
 *  Shares are queued so a second one arriving while the picker is open doesn't
 *  clobber the first (the user finishes/closes one, then the next opens). */
export default function ShareTargetModal() {
  const [queue, setQueue] = useState<ShareReceivedPayload[]>([]);

  useEffect(() => {
    const onShare = (p: ShareReceivedPayload) => setQueue((q) => [...q, p]);
    emitter.on(EVENTS.SHARE_RECEIVED, onShare);
    return () => emitter.off(EVENTS.SHARE_RECEIVED, onShare);
  }, []);

  const current = queue[0] || null;
  if (!current) return null;
  return (
    <ShareTargetPicker
      key={current.shareId}
      files={current.files}
      text={current.text}
      onClose={() => setQueue((q) => q.slice(1))}
    />
  );
}
